/**
 * AnalyticsDebugPanel
 *
 * Panel flotante para verificar en tiempo real qué eventos llegan a:
 *   1. window.dataLayer (GTM)
 *   2. Google Tag Manager → carga del contenedor
 *   3. GA4 (network requests a /g/collect o /collect)
 *
 * Activación: agrega `?debug=1` a la URL (ej: https://quedijomama.com/?debug=1)
 * O ejecuta en consola: localStorage.setItem('_analytics_debug', '1') y recarga.
 *
 * Mide:
 * - Cada push a dataLayer (intercepta dataLayer.push)
 * - Estado del contenedor GTM (gtm.load event)
 * - Requests salientes a GA4 (PerformanceObserver de tipo resource)
 */

import { useEffect, useRef, useState } from 'react';

type EventLog = {
  id: string;
  time: string;
  type: 'dl' | 'gtm' | 'ga4';
  name: string;
  payload?: Record<string, unknown>;
};

const MAX_EVENTS = 80;

const isDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === '1') {
      localStorage.setItem('_analytics_debug', '1');
      return true;
    }
    if (params.get('debug') === '0') {
      localStorage.removeItem('_analytics_debug');
      return false;
    }
    return localStorage.getItem('_analytics_debug') === '1';
  } catch {
    return false;
  }
};

const formatTime = (d: Date) =>
  `${d.getHours().toString().padStart(2, '0')}:${d
    .getMinutes()
    .toString()
    .padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}.${d
    .getMilliseconds()
    .toString()
    .padStart(3, '0')}`;

export const AnalyticsDebugPanel = () => {
  const [enabled] = useState(isDebugEnabled);
  const [events, setEvents] = useState<EventLog[]>([]);
  const [open, setOpen] = useState(true);
  const [filter, setFilter] = useState<'all' | 'dl' | 'gtm' | 'ga4'>('all');
  const [gtmLoaded, setGtmLoaded] = useState(false);
  const counters = useRef({ dl: 0, gtm: 0, ga4: 0 });
  const [, force] = useState(0);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const push = (entry: Omit<EventLog, 'id' | 'time'>) => {
      const log: EventLog = {
        id: crypto.randomUUID(),
        time: formatTime(new Date()),
        ...entry,
      };
      counters.current[entry.type]++;
      setEvents((prev) => [log, ...prev].slice(0, MAX_EVENTS));
      force((n) => n + 1);
    };

    // 1. Intercept dataLayer.push
    window.dataLayer = window.dataLayer || [];
    const originalPush = window.dataLayer.push.bind(window.dataLayer);
    window.dataLayer.push = function (...args: Record<string, unknown>[]) {
      args.forEach((a) => {
        const ev = (a && (a.event as string)) || 'unnamed';
        if (ev === 'gtm.load' || ev === 'gtm.dom' || ev === 'gtm.js') {
          push({ type: 'gtm', name: ev, payload: a });
          if (ev === 'gtm.load') setGtmLoaded(true);
        } else {
          push({ type: 'dl', name: ev, payload: a });
        }
      });
      return originalPush(...args);
    };

    // Replay buffer existente (eventos que llegaron antes de montar)
    try {
      (window.dataLayer as Record<string, unknown>[]).forEach((a) => {
        const ev = (a && (a.event as string)) || 'unnamed';
        if (ev === 'gtm.load') setGtmLoaded(true);
      });
    } catch {
      // ignore
    }

    // 2. Observa requests salientes (GA4 → /g/collect, GTM → gtm.js)
    let observer: PerformanceObserver | null = null;
    try {
      observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const url = entry.name;
          if (url.includes('googletagmanager.com/gtm.js')) {
            push({ type: 'gtm', name: 'gtm.js loaded', payload: { url } });
          } else if (
            url.includes('google-analytics.com/g/collect') ||
            url.includes('analytics.google.com/g/collect') ||
            url.includes('/collect?v=2')
          ) {
            // Extrae el event name del query string
            try {
              const u = new URL(url);
              const en = u.searchParams.get('en') || 'page_view';
              push({ type: 'ga4', name: en, payload: { url: url.slice(0, 200) } });
            } catch {
              push({ type: 'ga4', name: 'collect', payload: { url } });
            }
          }
        });
      });
      observer.observe({ entryTypes: ['resource'] });
    } catch {
      // ignore
    }

    return () => {
      window.dataLayer.push = originalPush;
      observer?.disconnect();
    };
  }, [enabled]);

  if (!enabled) return null;

  const filtered = filter === 'all' ? events : events.filter((e) => e.type === filter);

  const typeColor = (t: EventLog['type']) =>
    t === 'dl'
      ? 'bg-blue-500'
      : t === 'gtm'
      ? 'bg-amber-500'
      : 'bg-emerald-500';

  const typeLabel = (t: EventLog['type']) =>
    t === 'dl' ? 'DL' : t === 'gtm' ? 'GTM' : 'GA4';

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 12,
        right: 12,
        zIndex: 999999,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 11,
        color: '#fff',
        width: open ? 420 : 'auto',
        maxWidth: 'calc(100vw - 24px)',
        background: 'rgba(15, 15, 20, 0.96)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 10,
        boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '8px 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderBottom: open ? '1px solid rgba(255,255,255,0.08)' : 'none',
          cursor: 'pointer',
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: gtmLoaded ? '#10b981' : '#ef4444',
            display: 'inline-block',
          }}
          title={gtmLoaded ? 'GTM cargado' : 'GTM NO cargado'}
        />
        <strong style={{ fontSize: 12 }}>Analytics Debug</strong>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <span style={{ background: '#3b82f6', padding: '1px 6px', borderRadius: 4 }}>
            DL {counters.current.dl}
          </span>
          <span style={{ background: '#f59e0b', padding: '1px 6px', borderRadius: 4 }}>
            GTM {counters.current.gtm}
          </span>
          <span style={{ background: '#10b981', padding: '1px 6px', borderRadius: 4 }}>
            GA4 {counters.current.ga4}
          </span>
          <span style={{ opacity: 0.5, marginLeft: 4 }}>{open ? '▾' : '▸'}</span>
        </span>
      </div>

      {open && (
        <>
          {/* Filters */}
          <div
            style={{
              display: 'flex',
              gap: 4,
              padding: '6px 10px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            {(['all', 'dl', 'gtm', 'ga4'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  background:
                    filter === f ? 'rgba(255,255,255,0.15)' : 'transparent',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 4,
                  padding: '2px 8px',
                  fontSize: 10,
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                }}
              >
                {f}
              </button>
            ))}
            <button
              onClick={() => {
                setEvents([]);
                counters.current = { dl: 0, gtm: 0, ga4: 0 };
                force((n) => n + 1);
              }}
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 10,
                cursor: 'pointer',
              }}
            >
              Limpiar
            </button>
            <button
              onClick={() => {
                localStorage.removeItem('_analytics_debug');
                window.location.search = window.location.search
                  .replace(/[?&]debug=1/, '')
                  .replace(/^&/, '?');
              }}
              style={{
                background: 'transparent',
                color: '#ef4444',
                border: '1px solid rgba(239,68,68,0.4)',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 10,
                cursor: 'pointer',
              }}
            >
              Off
            </button>
          </div>

          {/* Event list */}
          <div
            style={{
              maxHeight: 360,
              overflowY: 'auto',
              padding: 6,
            }}
          >
            {filtered.length === 0 && (
              <div style={{ padding: 12, opacity: 0.5, textAlign: 'center' }}>
                Sin eventos aún. Interactúa con la página…
              </div>
            )}
            {filtered.map((e) => (
              <details
                key={e.id}
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 4,
                  marginBottom: 3,
                  padding: '4px 6px',
                }}
              >
                <summary
                  style={{
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                    cursor: 'pointer',
                    listStyle: 'none',
                  }}
                >
                  <span
                    className={typeColor(e.type)}
                    style={{
                      padding: '0 5px',
                      borderRadius: 3,
                      fontSize: 9,
                      fontWeight: 700,
                      minWidth: 28,
                      textAlign: 'center',
                    }}
                  >
                    {typeLabel(e.type)}
                  </span>
                  <span style={{ opacity: 0.5, fontSize: 10 }}>{e.time}</span>
                  <span style={{ flex: 1, fontWeight: 600 }}>{e.name}</span>
                </summary>
                <pre
                  style={{
                    margin: '4px 0 0',
                    padding: 6,
                    background: 'rgba(0,0,0,0.4)',
                    borderRadius: 3,
                    fontSize: 10,
                    maxHeight: 200,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {JSON.stringify(e.payload, null, 2)}
                </pre>
              </details>
            ))}
          </div>

          {/* Footer hint */}
          <div
            style={{
              padding: '6px 10px',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              fontSize: 10,
              opacity: 0.6,
              lineHeight: 1.4,
            }}
          >
            DL = dataLayer.push · GTM = contenedor · GA4 = /g/collect saliente.
            Activado por <code>?debug=1</code>.
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsDebugPanel;
